import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { AppIcon } from '@/components/AppIcon';
import { ReviewCards } from '@/components/ReviewCards';
import { UiTypography } from '@/components/ui/Typography';
import { amplitudeModule } from '@/modules/amplitude';
import { AnimationFill } from '@/pages/Onboarding/components/AnimationFill';
import {
  OnboardingOptionItem,
  OnboardingOptionsList,
} from '@/pages/Onboarding/components/List';
import { OnboardingSubtitle } from '@/pages/Onboarding/components/Subtitle';
import { useOnboarding } from '@/pages/Onboarding/context';
import { OwnAiTitle } from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataType } from '../../types';

import { ModalStyles, PageStyles } from './SettingUpProfile.styles';

const animationSteps = [
  {
    delay: 0,
    label: 'I’m reading your answers',
  },
  {
    delay: 4,
    label: "Deciding if you're worth my time",
  },
  {
    delay: 8,
    label: 'Judging your potential',
  },
];

export const MODAL_OPTIONS = [
  {
    id: 'yes',
    title: 'Yes, Mistress',
    value: true,
  },
  {
    id: 'no',
    title: 'No',
    value: false,
  },
];

export const ScreenSettingUpProfile: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Domination SettingUpProfile',
      });
    }
  }, []);

  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const form = useFormContext<LayoutDataType>();
  const readyToObey = form.watch('ready_to_obey');

  const { wizard } = useOnboarding();

  const handleNextStep = () => {
    nextStepTimeout(wizard.nextStep);
  };

  const onFinishAnimation = () => {
    setIsModalOpen(true);
  };

  const handleSelect = (option: {
    id: string;
    title: string;
    value: boolean;
  }) => {
    form.setValue('ready_to_obey', option.value);

    userService.createOrUpdate({
      onboarding_data: {
        ready_to_obey: option.value,
      },
    });

    setIsModalOpen(false);
    handleNextStep();
  };

  return (
    <PageStyles background="domination">
      <AppIcon className="app-logo" />
      <OwnAiTitle className="setting-up-title">I’m reviewing you</OwnAiTitle>
      <OnboardingSubtitle className="setting-up-subtitle">
        Stay still. Don’t make me repeat myself
      </OnboardingSubtitle>

      <IonContent>
        <AnimationFill
          onComplete={onFinishAnimation}
          show
          steps={animationSteps}
          stepDuration={4}
          progressBarStyles={{
            backgroundImage:
              'linear-gradient(95.02deg, #FF1E4F 0%, #9B102F 100%)',
          }}
          progressStyles={{
            background: 'rgba(255, 255, 255, 0.1)',
          }}
        />

        <section className="setting-up-user-reviews">
          <UiTypography variant="title-large" align="center" transform="bold">
            She left a mark on them
          </UiTypography>
          <ReviewCards color={'domination'} />
        </section>
        <ModalStyles
          isOpen={isModalOpen}
          onDidDismiss={() => {
            setIsModalOpen(false);
          }}
        >
          <div className="modal-wrapper">
            <img
              src={'/assets/layout-own-ai-dominate/modal-image.png'}
              alt="Domination modal"
            />
            <UiTypography variant="headline-small" align="center">
              I have made my choice.
              <br />
              Are you ready to obey?
            </UiTypography>
            <OnboardingOptionsList>
              {MODAL_OPTIONS.map((option) => (
                <OnboardingOptionItem
                  checked={readyToObey === option.value}
                  control={form.control}
                  option={option}
                  name="ready_to_obey"
                  key={option.id}
                  onChange={() => handleSelect(option)}
                  TEST_ID={`ready-to-obey-${option.id}`}
                  theme={'domination'}
                />
              ))}
            </OnboardingOptionsList>
          </div>
        </ModalStyles>
      </IonContent>
    </PageStyles>
  );
};
